/* eslint-disable id-blacklist */
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CancellationReason, ReservationService, RideRequestService } from '@uzurv/clara';
import {
  CancellationReasonConstants,
  PermissionsConstants,
  ReservationStatusNameConstants,
  TimeUtil,
  VehicleTypeConstants,
  isNullOrUndefined
} from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import moment from 'moment-timezone';
import { ReservationListItemVm } from '../../reservation';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';

export interface CancelReservationDialogData {
  ride: ReservationListItemVm;
}

export interface CancelReservationDialogOutput {
  isRecovery: boolean;
  isIncident: boolean;
  cancellationReason: CancellationReason;
  note: string;
  ride: ReservationListItemVm;
}

@Component({
  selector: 'app-cancel-reservation-dialog',
  templateUrl: './cancel-reservation-dialog.component.html',
  styleUrls: ['./cancel-reservation-dialog.component.scss']
})
export class CancelReservationDialogComponent implements OnInit, OnDestroy {
  cancellationForm: FormGroup<{
    cancellationReason: FormControl<CancellationReason>;
    cancellationReasonDetails: FormControl<string>;
  }>;

  cancellationReasons: Array<CancellationReason> = [];
  shouldShowCancellationReasonsLoadingSpinner = false;
  cancellationReasonDetails: string;
  minutesAfterPickupDateToDisplayRiderNoShow = -10;
  minutesAfterPickupDateToDisplayDriverNoShow = 0;

  reservationStatuses = [];

  private disabledCancellationReasons: Array<string> = [];
  private refreshTimeoutDuration: number = 30 * 1000;
  private refreshNoShowOptionsTimer: number;

  constructor(
    private reservationService: ReservationService,
    private rideRequestService: RideRequestService,
    @Inject(MAT_DIALOG_DATA) public data: CancelReservationDialogData,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<CancelReservationDialogComponent>,
    private authDataService: AuthDataService
  ) {}

  ngOnInit() {
    this.getCancellationReasons();
    this.refreshDisabledCancellationReasons();

    this.reservationService.getReservationStatuses().subscribe(statuses => {
      statuses.forEach(status => this.reservationStatuses.push(status));
    });

    this.cancellationForm = new FormGroup({
      cancellationReason: new FormControl(null, [Validators.required]),
      cancellationReasonDetails: new FormControl('')
    });

    this.cancellationForm.controls.cancellationReason.valueChanges.subscribe(value => {
      if (value) {
        this.cancellationForm.controls.cancellationReasonDetails.setValidators(
          value.name === CancellationReasonConstants.DRIVER_NO_SHOW ||
            value.name === CancellationReasonConstants.RIDE_INCIDENT
            ? [Validators.required, Validators.minLength(10), Validators.maxLength(1000)]
            : null
        );
      } else {
        this.cancellationForm.controls.cancellationReasonDetails.clearValidators();
      }
      this.cancellationForm.controls.cancellationReasonDetails.updateValueAndValidity();
    });
  }

  ngOnDestroy() {
    if (!isNullOrUndefined(this.refreshNoShowOptionsTimer)) {
      window.clearTimeout(this.refreshNoShowOptionsTimer);
    }
  }

  get shouldShowCreateRecovery(): boolean {
    if (
      this.cancellationForm.controls.cancellationReason.value &&
      (this.cancellationForm.controls.cancellationReason.value.name === CancellationReasonConstants.DRIVER_NO_SHOW ||
        this.cancellationForm.controls.cancellationReason.value.name === CancellationReasonConstants.RIDE_INCIDENT)
    ) {
      return true;
    }
    return false;
  }

  get detailsErrorText(): string {
    const errors = this.cancellationForm.controls.cancellationReasonDetails.errors;
    if (!isNullOrUndefined(errors['required'])) {
      return 'A note is required';
    }
    if (!isNullOrUndefined(errors['minlength'])) {
      return 'Please use at least 10 characters';
    }
    if (!isNullOrUndefined(errors['maxlength'])) {
      return 'Note cannot exceed 1000 characters';
    }
    return '';
  }

  get rideType(): string {
    return this.data.ride.rideRequestDetail.accepted ? 'Reservation' : 'Ride Request';
  }

  isCancellationReasonEnabled(cancellationReason: CancellationReason): boolean {
    return !this.disabledCancellationReasons.includes(cancellationReason.name);
  }

  closeModal() {
    this.dialogRef.close();
  }

  cancelRide(): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are You Sure?',
          message: this.generateConfirmationMessage(),
          isDestructive: false
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({
            isRecovery: false,
            cancellationReason: this.cancellationForm.controls.cancellationReason.value,
            note: this.cancellationForm.controls.cancellationReasonDetails.value,
            ride: this.data.ride
          } as CancelReservationDialogOutput);
        }
      });
  }

  cancelRideWithRecovery(): void {
    this.dialogRef.close({
      isRecovery: true,
      isIncident: this.cancellationForm.controls.cancellationReason.value.name === CancellationReasonConstants.RIDE_INCIDENT,
      cancellationReason: this.cancellationForm.controls.cancellationReason.value,
      note: this.cancellationForm.controls.cancellationReasonDetails.value,
      ride: this.data.ride
    } as CancelReservationDialogOutput);
  }

  shouldShowCancellationReasonDetails(): boolean {
    return (
      this.cancellationForm.controls.cancellationReason.value &&
      (this.cancellationForm.controls.cancellationReason.value.name === CancellationReasonConstants.DRIVER_NO_SHOW ||
        this.cancellationForm.controls.cancellationReason.value.name === CancellationReasonConstants.RIDE_INCIDENT
      )
    );
  }

  private refreshDisabledCancellationReasons() {
    if (!isNullOrUndefined(this.refreshNoShowOptionsTimer)) {
      window.clearTimeout(this.refreshNoShowOptionsTimer);
    }

    this.disabledCancellationReasons = [];

    if (this.isOutsideNoShowWindowMinutes(this.minutesAfterPickupDateToDisplayRiderNoShow)) {
      this.disabledCancellationReasons.push(CancellationReasonConstants.RIDER_NO_SHOW);
    }

    if (this.isOutsideNoShowWindowMinutes(this.minutesAfterPickupDateToDisplayDriverNoShow)) {
      this.disabledCancellationReasons.push(CancellationReasonConstants.DRIVER_NO_SHOW);
    }

    this.refreshNoShowOptionsTimer = window.setTimeout(() => {
      this.refreshDisabledCancellationReasons();
    }, this.refreshTimeoutDuration);
  }

  private getCancellationReasons() {
    if (!isNullOrUndefined(this.data.ride.driver)) {
      this.reservationService.listReservationCancellationReasons().subscribe(
        cancellationReasons => (this.cancellationReasons = this.filterCancellationMethodsByPermissionAndStatus(cancellationReasons)),
        err => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: err.message
            }
          });
        }
      );
    } else {
      this.rideRequestService.listRideRequestCancellationReasons().subscribe(
        cancellationReasons => (this.cancellationReasons = this.filterCancellationMethodsByPermissionAndStatus(cancellationReasons)),
        err => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: err.message
            }
          });
        }
      );
    }
  }

  private generateConfirmationMessage(): string {
    let message = `Are you sure you want to cancel this ${this.rideType}  as ${this.cancellationForm.controls.cancellationReason.value.name}?`;

    if (isNullOrUndefined(this.data.ride.driver)) {
      return message;
    }

    let feeConfirmation = '';

    switch (this.cancellationForm.controls.cancellationReason.value.name) {
      case CancellationReasonConstants.DRIVER_CANCELLED: {
        feeConfirmation = `
          The driver may be charged a $${this.data.ride.providerFeeSchedule.cancellationFee} cancellation fee
          if the reservation is less than ${TimeUtil.secondsToDurationString(
            this.data.ride.transitProgramInfo.providerCancellationWindowMinutes * 60
          )} from now
        `;
        break;
      }
      case CancellationReasonConstants.RIDER_CANCELLED: {
        feeConfirmation = `
          The rider may be charged a $${this.data.ride.clientFeeSchedule.riderCancellationFee} cancellation fee
          if the ${this.rideType} is less than ${TimeUtil.secondsToDurationString(
          this.data.ride.transitProgramInfo.riderCancellationWindowMinutes * 60
        )} from now
        `;

        break;
      }
      case CancellationReasonConstants.DRIVER_NO_SHOW: {
        feeConfirmation = `The driver will be charged a $${this.data.ride.providerFeeSchedule.cancellationFee} cancellation fee`;
        break;
      }
      case CancellationReasonConstants.RIDER_NO_SHOW: {
        feeConfirmation = `The rider will be charged a $${this.data.ride.clientFeeSchedule.riderCancellationFee} cancellation fee`;
        break;
      }
    }

    if (feeConfirmation.length > 0) {
      message += '<br /><br />' + feeConfirmation;
    }

    return message;
  }

  private isOutsideNoShowWindowMinutes(noShowWindowMinutes): boolean {
    const minNoShowDate = moment(this.data.ride.rideRequestDetail.pickupDate).add(noShowWindowMinutes, 'minutes').toDate();
    return new Date() < minNoShowDate;
  }

  private filterCancellationMethodsByPermissionAndStatus(cancellationReasons: Array<CancellationReason>): Array<CancellationReason> {
    if (
      this.data.ride.status !== ReservationStatusNameConstants.IN_PROGRESS ||
      this.data.ride.rideRequestDetail.vehicleType.code === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV ||
      this.data.ride.rideRequestDetail.dispatchedToLyft
    ) {
      cancellationReasons = cancellationReasons.filter(reason => reason.name !== CancellationReasonConstants.RIDE_INCIDENT);
    }
    if (this.authDataService.hasPermission({ any: ['admin_cancel'], all: [] })) {
      return this.sortCancellationReasons(cancellationReasons);
    } else if (this.authDataService.hasPermission({ any: [], all: [PermissionsConstants.WEB_SELF_SERVICE_RIDE_CANCEL] })) {
      return this.sortCancellationReasons(
        cancellationReasons.filter(reason => reason.name === CancellationReasonConstants.RIDER_CANCELLED)
      );
    } else {
      return this.sortCancellationReasons(
        cancellationReasons.filter(
          reason =>
            reason.name !== CancellationReasonConstants.LYFT_DISPATCH_DRIVER_CANCELLED &&
            reason.name !== CancellationReasonConstants.LYFT_DISPATCH_STATUS_UNKNOWN &&
            reason.name !== CancellationReasonConstants.LYFT_DISPATCH_STATUS_FAILED &&
            reason.name !== CancellationReasonConstants.RIDE_REQUEST_EXPIRED &&
            reason.name !== CancellationReasonConstants.RESERVATION_EXPIRED &&
            reason.name !== CancellationReasonConstants.WILL_CALL_EXPIRED &&
            reason.name !== CancellationReasonConstants.RIDE_MODIFIED &&
            reason.name !== CancellationReasonConstants.ADMIN_CANCELLED
        )
      );
    }
  }

  private sortCancellationReasons(cancellationReasons: Array<CancellationReason>): Array<CancellationReason> {
    const order = [
      CancellationReasonConstants.ADMIN_CANCELLED,
      CancellationReasonConstants.DRIVER_CANCELLED,
      CancellationReasonConstants.DRIVER_NO_SHOW,
      CancellationReasonConstants.RIDER_NO_SHOW,
      CancellationReasonConstants.RIDER_CANCELLED,
      CancellationReasonConstants.RIDE_INCIDENT
    ];

    return cancellationReasons.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }
}
